import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import { useOwnezzyContact, whatsappLink } from '../legal/contact';
import './AboutPage.css';
import './ContactPage.css';

// /contact on ownezzy.com: how shop owners, partners and shoppers reach Ownezzy. The contact details come from the
// Super Admin's settings (legal/contact.js). The message form needs no server: it opens WhatsApp (or email) with the
// message already typed.
const TITLE = 'Contact Ownezzy | WhatsApp, Email and Support';
const DESCRIPTION = 'Contact the Ownezzy team on WhatsApp or email for help with your free online store, plans, partnerships or a complaint about a store hosted on Ownezzy.';

const TOPICS = ['Help setting up my store', 'Plans and payments', 'Become a partner', 'Report a store or a complaint', 'Something else'];

const FAQ = [
  ['How fast do you reply?', 'On WhatsApp usually within a few hours, every day from 9 AM to 9 PM. Emails are answered within one working day.'],
  ['I bought something from a store on Ownezzy. Who do I contact?', 'Each store is run by its own seller, who handles orders, delivery and refunds. Use the contact details on that store first. If the seller does not respond or you were cheated, tell us below with the store address and we will look into it.'],
  ['Can you build my store for me?', 'Yes. Pick a design, and our team can help you add your products, logo and photos on WhatsApp.'],
];

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

export default function ContactPage() {
  const contact = useOwnezzyContact();
  const [form, setForm] = useState({ name: '', topic: TOPICS[0], message: '' });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const text = `Hi Ownezzy, I'm ${form.name || 'a shop owner'}.\nTopic: ${form.topic}\n\n${form.message}`.trim();

  useEffect(() => {
    document.title = TITLE;
    setMeta('meta[name="description"]', 'content', DESCRIPTION);
    setMeta('link[rel="canonical"]', 'href', 'https://ownezzy.com/contact');
    window.scrollTo(0, 0);
  }, []);

  const sendWhatsapp = (e) => {
    e.preventDefault();
    window.open(whatsappLink(contact, text), '_blank', 'noopener');
  };

  return (
    <div className="app-shell ab-page">
      <AnnouncementBar />
      <Navbar />
      <main>
        <section className="ab-hero ct-hero">
          <div className="container">
            <div className="ab-crumbs"><a href="/">Home</a> / Contact us</div>
            <h1>We are here to <span>help you sell</span></h1>
            <p>Questions about your store, a plan, becoming a partner or anything else? Talk to a real person on WhatsApp or write to us.</p>
          </div>
        </section>

        <section className="container ct-cards">
          <a className="ct-card ct-whatsapp" href={whatsappLink(contact, 'Hi Ownezzy, I need help.')} target="_blank" rel="noreferrer">
            <span className="ct-icon"><i className="bi bi-whatsapp" /></span>
            <b>Chat on WhatsApp</b>
            <small>{contact.whatsapp}</small>
            <em>Fastest · 9 AM to 9 PM, every day</em>
          </a>
          <a className="ct-card" href={`mailto:${contact.email}`}>
            <span className="ct-icon"><i className="bi bi-envelope-paper-heart" /></span>
            <b>Email us</b>
            <small>{contact.email}</small>
            <em>Reply within one working day</em>
          </a>
          <a className="ct-card" href="/partner">
            <span className="ct-icon"><i className="bi bi-people" /></span>
            <b>Become a partner</b>
            <small>Earn on every website you sell</small>
            <em>See how it works →</em>
          </a>
        </section>

        {/* Who runs Ownezzy, as payment providers and the consumer rules ask; the policies are linked here too */}
        <section className="container ct-business">
          <h2>Business details</h2>
          <ul>
            <li><b>Business</b> Ownezzy, website software and hosting for small businesses</li>
            <li><b>Operated by</b> {contact.operator} (sole proprietor), India</li>
            <li><b>Email</b> <a href={`mailto:${contact.email}`}>{contact.email}</a></li>
            <li><b>WhatsApp / phone</b> {contact.whatsapp} · 9 AM to 9 PM, every day</li>
            <li><b>Payments</b> UPI, Indian and international cards, net banking and wallets. Prices in Indian Rupees (₹).</li>
          </ul>
          <p>
            <a href="/terms">Terms of Service</a> · <a href="/privacy">Privacy Policy</a> · <a href="/refund-policy">Cancellation &amp; Refund Policy</a> · <a href="/delivery-policy">Delivery Policy</a>
          </p>
        </section>

        <section className="container ct-grid">
          <form className="ct-form" onSubmit={sendWhatsapp}>
            <span className="ab-kicker">SEND A MESSAGE</span>
            <h2>Tell us how we can help</h2>
            <label>Your name<input value={form.name} onChange={set('name')} placeholder="e.g. Priya Sharma" maxLength={80} /></label>
            <label>What is it about?
              <select value={form.topic} onChange={set('topic')}>{TOPICS.map((t) => <option key={t}>{t}</option>)}</select>
            </label>
            <label>Your message<textarea rows={5} value={form.message} onChange={set('message')} placeholder="Write your question. For a complaint about a store, add its web address and your order number." maxLength={1000} required /></label>
            <div className="ct-actions">
              <button type="submit" className="ct-send"><i className="bi bi-whatsapp" /> Send on WhatsApp</button>
              <a className="ct-send ct-send-email" href={`mailto:${contact.email}?subject=${encodeURIComponent(form.topic)}&body=${encodeURIComponent(text)}`}><i className="bi bi-envelope" /> Send by email</a>
            </div>
            <small className="ct-note">Your message opens in WhatsApp or your email app, ready to send. We never share your details.</small>
          </form>

          <div className="ct-side">
            <div className="ct-box">
              <h3><i className="bi bi-shield-check" /> Grievance Officer</h3>
              <p>To report unlawful content, fraud or a prohibited product on a store hosted by Ownezzy, write to <a href={`mailto:${contact.email}`}>{contact.email}</a> or WhatsApp {contact.whatsapp}. We acknowledge complaints within 24 hours and aim to resolve them within 15 days.</p>
              <a href="/terms">Read our Terms of Service →</a>
            </div>
            <div className="ct-box">
              <h3><i className="bi bi-question-circle" /> Common questions</h3>
              {FAQ.map(([q, a]) => (
                <details key={q}>
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
